import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useFrfrViewMultimode } from "../hooks/useFrfrViewMultimode";
import { dmd_utils } from "../utils/frfr-dmd-utils";
import { js_utils } from "../utils/frfr-js-utils";
import { frfr_res_utils } from "../utils/frfr-res-utils";
import { ACCEPT_FILE_TYPES_IMG_JPG_PNG } from "../utils/frfr-string-constants";
import { _zzyourmap_ajax, _zzyourmap_urlLib } from "../utils/zz/zz-yourmap-ajax-adapter";
import { _zzyourmap_prepare_entbox_for_submit_image_library_meta, _zzyourmap_formDefinition_formPart_editing, _zzyourmap_formDefinition_image_library_meta, _zzyourmap_preset_image_library_meta } from "../utils/zz/zz-yourmap-admin-form-utils";
import { _zzyourmap_prepare_image_library_entities } from "../utils/zz/zz-yourmap-image-utils";
import { _zzyourmap_lib_image_library_tags_default } from "../utils/zz/zz-yourmap-data-keys-and-values-lib";
import FormGenerator from "./FormGenerator";
import Komentar from "./Komentar";
import UniImage from "./UniImage";
import UniSpinnerClassic from "./UniSpinnerClassic";


// ---- Helpers ----

const _prepare_img_meta_for_edit = (item) => {
  const editing_entity = { ..._zzyourmap_preset_image_library_meta };
  if (item.id > 0) { editing_entity.id = item.id; }
  editing_entity.img_alt = js_utils.fix_always_string(item.img_alt);
  editing_entity.img_title = js_utils.fix_always_string(item.img_title);
  editing_entity.note = js_utils.fix_always_string(item.note);
  editing_entity.tags_csv = js_utils.fix_always_string(item.tags_csv);
  return editing_entity;
};


// ---- Sorting ----

const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'name_asc', label: 'Name (A\u2013Z)' },
  { value: 'name_desc', label: 'Name (Z\u2013A)' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'tags_asc', label: 'Tags (A\u2013Z)' },
  { value: 'tags_desc', label: 'Tags (Z\u2013A)' },
];

// Returns the first tag (alphabetically sorted) from a tags_csv string,
// or null if the item has no tags. Used for tag-based sorting.
const _first_tag = (tags_csv) => {
  if (!tags_csv) return null;
  const parts = tags_csv.split(',').map(t => t.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return parts.slice().sort((a, b) => a.localeCompare(b))[0];
};

// Sorts a copy of arr — never mutates the original.
// Relies only on client-side data; no backend calls are made.
const _sort_arr = (arr, sortBy) => {
  if (!Array.isArray(arr) || sortBy === 'default') return [...arr];
  const copy = [...arr];
  switch (sortBy) {
    case 'name_asc': return copy.sort((a, b) => (a.img_filename || '').localeCompare(b.img_filename || ''));
    case 'name_desc': return copy.sort((a, b) => (b.img_filename || '').localeCompare(a.img_filename || ''));
    case 'newest': return copy.sort((a, b) => b.id - a.id);
    case 'oldest': return copy.sort((a, b) => a.id - b.id);
    // Items with no tags are pushed to the end in both tag sort directions
    case 'tags_asc': return copy.sort((a, b) => {
      const ta = _first_tag(a.tags_csv), tb = _first_tag(b.tags_csv);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta.localeCompare(tb);
    });
    case 'tags_desc': return copy.sort((a, b) => {
      const ta = _first_tag(a.tags_csv), tb = _first_tag(b.tags_csv);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb.localeCompare(ta);
    });
    default: return copy;
  }
};


// ---- Tags Editor (shared between Add and Edit forms) ----

const TagsEditor = ({ tagsValue, onAdd, onDelete, tags_arr_all = [] }) => {
  const addedTags = js_utils.string_split_to_arr_but_exclude_empty(tagsValue, ',');
  const pickTags = Array.isArray(tags_arr_all) ? tags_arr_all : [];
  return (
    <div className="ymil4__tags-editor">
      <div className="ymil4__tags-editor__label">Tags</div>
      <div className="ymil4__tags-editor__added">
        {addedTags.map(tag => (
          <div key={tag} className="ymil4__tags-editor__tag ymil4__tags-editor__tag--added">
            {tag}
            <i onClick={() => onDelete(tag)} className="fa fa-times-circle" aria-hidden="true"></i>
          </div>
        ))}
        {addedTags.length === 0 && (
          <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No tags added</span>
        )}
      </div>
      <div className="ymil4__tags-editor__pick-label">Pick tags:</div>
      <div className="ymil4__tags-editor__pick">
        {pickTags.map(tag => (
          <div key={tag} className="ymil4__tags-editor__tag ymil4__tags-editor__tag--pick" onClick={() => onAdd(tag)}>
            <i className="fa fa-plus" aria-hidden="true"></i>{tag}
          </div>
        ))}
      </div>
    </div>
  );
};


// ---- FormEditMeta (rendered inside modal) ----

const FormEditMeta = (props) => {
  const { cbSubmitUpdateImageMeta, cbDialogCancel, preview_src, tags_arr_all } = props;

  let initialState;
  let formDefinition;
  let editMode = false;
  if (props.mode === 'EDIT') {
    editMode = true;
    initialState = props.editorData;
    formDefinition = [..._zzyourmap_formDefinition_formPart_editing, ..._zzyourmap_formDefinition_image_library_meta];
  } else {
    initialState = { ..._zzyourmap_preset_image_library_meta };
    formDefinition = _zzyourmap_formDefinition_image_library_meta;
  }

  const [formStateMeta, setFormStateMeta] = useState(initialState);
  const handleChangeFormStateMeta = (e) => {
    const { name, value } = e.target;
    setFormStateMeta(s => ({ ...s, [name]: value }));
  };

  const handleTagsAdd = (new_tag) => {
    setFormStateMeta(state => {
      if (js_utils.is_string_not_empty(new_tag) && !new_tag.includes(',') && !new_tag.includes(';') && !new_tag.includes(' ')) {
        const _new_tag = new_tag.trim().toLowerCase();
        const tags_arr = js_utils.string_split_to_arr_but_exclude_empty(state.tags_csv, ',');
        if (!tags_arr.includes(_new_tag) && js_utils.is_string_not_empty(_new_tag)) {
          tags_arr.push(_new_tag);
          tags_arr.sort();
          return { ...state, tags_csv: tags_arr.join(',') };
        }
      }
      return state;
    });
  };

  const handleTagsDelete = (target_tag) => {
    setFormStateMeta(state => {
      const tags_arr = js_utils.string_split_to_arr_but_exclude_empty(state.tags_csv, ',');
      if (tags_arr.includes(target_tag)) {
        return { ...state, tags_csv: tags_arr.filter(t => t !== target_tag).join(',') };
      }
      return state;
    });
  };

  return (
    <div className="ymil4__edit-meta unicompo-various-content">
      <h3>Edit image META</h3>
      <div className="ymil4__edit-meta__preview">
        <div className="ymil4__edit-meta__preview__label">Preview</div>
        <UniImage src={preview_src} extraClass="yourmap__theme__image-lib-preview" />
      </div>
      <FormGenerator
        formDefinition={formDefinition}
        formState={formStateMeta}
        formErrors={{}}
        handleChange={handleChangeFormStateMeta}
        tagsSuggestionsByFieldName={{ tags_csv: tags_arr_all }}
      />
      <TagsEditor
        tagsValue={formStateMeta.tags_csv}
        onAdd={handleTagsAdd}
        onDelete={handleTagsDelete}
        tags_arr_all={tags_arr_all}
      />
      <Komentar>
        <p>- img_alt is not needed for map widget images but helps with identification.</p>
        <p>- img_title can be shown as caption below images in articles.</p>
        <p>- Tags are for organization only and have no other significance.</p>
      </Komentar>
      <div className="ymil4__edit-meta__actions">
        <button
          className="ymil4__btn-primary"
          onClick={() => {
            const prepared_entbox = _zzyourmap_prepare_entbox_for_submit_image_library_meta(formStateMeta);
            if (editMode) {
              cbSubmitUpdateImageMeta(prepared_entbox, { id: formStateMeta.id });
            }
          }}
        >
          <i className="fa fa-paper-plane"></i> Save changes
        </button>
        <button className="ymil4__btn-secondary" onClick={() => cbDialogCancel()}>
          <i className="fa fa-times"></i> Cancel
        </button>
      </div>
    </div>
  );
};


// ---- Single Image Card (grid view) ----

const ImageCard = ({ item, item_key, src, itemPack, tags_arr_all }) => {
  const {
    enableSelectSingular, enableSelectMultiple, enableAdminFunc, isRecycleBin,
    _isRowSelected, _handleClickSelectRow, _handleClickDeleteImage,
    _handleClickUndeleteImage, cbSubmitUpdateImageMeta,
    setUniModalPortableContent_3, setUniModalPortableClose_3,
  } = itemPack;

  const isSelected = _isRowSelected(item_key);
  const isSelectedSingular = _isRowSelected(item_key, true);
  const highlighted = isSelected || isSelectedSingular;

  const tags = js_utils.string_split_to_arr_but_exclude_empty(item.tags_csv || '', ',');

  return (
    <div className={`ymil4__card${highlighted ? ' ymil4__card--selected' : ''}`}>
      <div className="ymil4__card__thumb">
        <UniImage src={src} extraClass="" />
        <div className="ymil4__card__overlay" />

        {/* Selection control */}
        {(enableSelectSingular || enableSelectMultiple) && (
          <div className="ymil4__card__select-ctrl">
            {enableSelectSingular && (
              <input
                type="radio"
                name="select_image_lib_item_singular"
                checked={isSelectedSingular}
                onChange={() => _handleClickSelectRow(item_key, true)}
              />
            )}
            {enableSelectMultiple && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => _handleClickSelectRow(item_key)}
              />
            )}
          </div>
        )}

        {/* Selected badge */}
        {highlighted && (
          <div className="ymil4__card__selected-badge">
            <i className="fa fa-check" aria-hidden="true"></i>
          </div>
        )}

        {/* Hover action buttons */}
        <div className="ymil4__card__actions">
          <button
            className="ymil4__card__action-btn ymil4__card__action-btn--edit"
            title="Edit META"
            onClick={(e) => {
              e.stopPropagation();
              setUniModalPortableContent_3({
                options: { large: true, zIndex: 22 },
                jsx: (
                  <FormEditMeta
                    mode="EDIT"
                    editorData={_prepare_img_meta_for_edit(item)}
                    preview_src={src}
                    tags_arr_all={tags_arr_all}
                    cbSubmitUpdateImageMeta={cbSubmitUpdateImageMeta}
                    cbDialogCancel={setUniModalPortableClose_3}
                  />
                ),
              });
            }}
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </button>
          {enableAdminFunc && (
            isRecycleBin ? (
              <button
                className="ymil4__card__action-btn ymil4__card__action-btn--undelete"
                title="Restore"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof _handleClickUndeleteImage === 'function') {
                    if (window.confirm('Restore this image?')) {
                      _handleClickUndeleteImage(item_key);
                    }
                  }
                }}
              >
                <i className="fa fa-undo" aria-hidden="true"></i>
              </button>
            ) : (
              <button
                className="ymil4__card__action-btn ymil4__card__action-btn--delete"
                title="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof _handleClickDeleteImage === 'function') {
                    if (window.confirm('Delete this image?')) {
                      _handleClickDeleteImage(item_key);
                    }
                  }
                }}
              >
                <i className="fa fa-trash" aria-hidden="true"></i>
              </button>
            )
          )}
        </div>
      </div>

      {/* Card info strip */}
      <div className="ymil4__card__info">
        <div className="ymil4__card__filename" title={item.img_filename}>
          {item.img_filename || `id: ${item.id}`}
        </div>
        <div className="ymil4__card__id">id: {item.id}</div>
        {tags.length > 0 && (
          <div className="ymil4__card__tags">
            {tags.slice(0, 3).map(t => (
              <span key={t} className="ymil4__card__tag">{t}</span>
            ))}
            {tags.length > 3 && (
              <span className="ymil4__card__tag">+{tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ---- Single Image Row (details / list view) ----

const ImageRow = ({ item, item_key, src, itemPack, tags_arr_all }) => {
  const {
    enableSelectSingular, enableSelectMultiple, enableAdminFunc, isRecycleBin,
    _isRowSelected, _handleClickSelectRow, _handleClickDeleteImage,
    _handleClickUndeleteImage, cbSubmitUpdateImageMeta,
    setUniModalPortableContent_3, setUniModalPortableClose_3,
  } = itemPack;

  const isSelected = _isRowSelected(item_key);
  const isSelectedSingular = _isRowSelected(item_key, true);
  const highlighted = isSelected || isSelectedSingular;

  const tags = js_utils.string_split_to_arr_but_exclude_empty(item.tags_csv || '', ',');

  return (
    <div className={`ymil4__row${highlighted ? ' ymil4__row--selected' : ''}`}>
      {/* Selection */}
      {(enableSelectSingular || enableSelectMultiple) && (
        <div className="ymil4__row__select-ctrl">
          {enableSelectSingular && (
            <input
              type="radio"
              name="select_image_lib_item_singular"
              checked={isSelectedSingular}
              onChange={() => _handleClickSelectRow(item_key, true)}
            />
          )}
          {enableSelectMultiple && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => _handleClickSelectRow(item_key)}
            />
          )}
        </div>
      )}

      {/* Thumbnail */}
      <div className="ymil4__row__thumb">
        <UniImage src={src} extraClass="" />
      </div>

      {/* Details */}
      <div className="ymil4__row__details">
        <div className="ymil4__row__filename">
          <a href={src} target="_blank" rel="noopener noreferrer">{item.img_filename}</a>
          <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 11 }}>#{item.id}</span>
        </div>
        {item.img_alt && <div className="ymil4__row__meta"><b>alt:</b> {item.img_alt}</div>}
        {item.img_title && <div className="ymil4__row__meta"><b>title:</b> {item.img_title}</div>}
        {tags.length > 0 && (
          <div className="ymil4__row__tags">
            {tags.map(t => <span key={t} className="ymil4__row__tag">{t}</span>)}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="ymil4__row__actions">
        <button
          className="ymil4__row__action-btn"
          title="Edit META"
          onClick={() => {
            setUniModalPortableContent_3({
              options: { large: true, zIndex: 22 },
              jsx: (
                <FormEditMeta
                  mode="EDIT"
                  editorData={_prepare_img_meta_for_edit(item)}
                  preview_src={src}
                  tags_arr_all={tags_arr_all}
                  cbSubmitUpdateImageMeta={cbSubmitUpdateImageMeta}
                  cbDialogCancel={setUniModalPortableClose_3}
                />
              ),
            });
          }}
        >
          <i className="fa fa-pencil" aria-hidden="true"></i>
        </button>
        {enableAdminFunc && (
          isRecycleBin ? (
            <button
              className="ymil4__row__action-btn ymil4__row__action-btn--success"
              title="Restore"
              onClick={() => {
                if (typeof _handleClickUndeleteImage === 'function') {
                  if (window.confirm('Restore this image?')) {
                    _handleClickUndeleteImage(item_key);
                  }
                }
              }}
            >
              <i className="fa fa-undo" aria-hidden="true"></i>
            </button>
          ) : (
            <button
              className="ymil4__row__action-btn ymil4__row__action-btn--danger"
              title="Delete"
              onClick={() => {
                if (typeof _handleClickDeleteImage === 'function') {
                  if (window.confirm('Delete this image?')) {
                    _handleClickDeleteImage(item_key);
                  }
                }
              }}
            >
              <i className="fa fa-trash" aria-hidden="true"></i>
            </button>
          )
        )}
      </div>
    </div>
  );
};


// ---- Image Library View (grid / list + pagination) ----

const ImageLibraryView = (props) => {
  const {
    viewArgs,
    showDetails,
    tags_arr_all,
    tags_arr_used,
  } = props;
  const {
    _fetching, rowsData, dataFreshness,
    cbOnSelectionChange, cbOnSelectionChangeSingular,
  } = viewArgs;

  const _viewBlueprint = {
    general: {
      disableBlueprintCaching: false,
      selectAllSelectOnlyVisible: true,
      pagination_enabled: true,
      pagination_rowsPerPageInitial: 20,
    },
    row: {},
  };

  const {
    fetching,
    rowsFinalReadyArr,
    paginationPack,
    _isRowSelected,
    _handleClickSelectRow,
  } = useFrfrViewMultimode(_fetching, _viewBlueprint, rowsData, dataFreshness, cbOnSelectionChange, cbOnSelectionChangeSingular);

  const {
    pagination_per_page,
    pagination_page_no,
    pagination_prev_page_no,
    pagination_next_page_no,
    pagination_pages_count,
    _handleChangePagination_rowsPerPage,
    _handleClickPaginationPagePrev,
    _handleClickPaginationPageNext,
  } = paginationPack || {};

  const totalCount = Array.isArray(rowsData) ? rowsData.length : 0;
  const perPage = pagination_per_page || 20;
  const pageNo = pagination_page_no || 1;
  const fromItem = totalCount === 0 ? 0 : (pageNo - 1) * perPage + 1;
  const toItem = Math.min(pageNo * perPage, totalCount);

  const itemPack = {
    enableSelectSingular: props.enableSelectSingular,
    enableSelectMultiple: props.enableSelectMultiple,
    enableAdminFunc: props.enableAdminFunc,
    isRecycleBin: props.isRecycleBin,
    _isRowSelected,
    _handleClickSelectRow,
    _handleClickDeleteImage: props._handleClickDeleteImage,
    _handleClickUndeleteImage: props._handleClickUndeleteImage,
    cbSubmitUpdateImageMeta: props.cbSubmitUpdateImageMeta,
    setUniModalPortableContent_3: props.setUniModalPortableContent_3,
    setUniModalPortableClose_3: props.setUniModalPortableClose_3,
  };

  return (
    <div>
      {/* Grid or List */}
      {fetching ? (
        <div className="ymil4__spinner-overlay">
          <UniSpinnerClassic />&nbsp;Loading...
        </div>
      ) : rowsFinalReadyArr.length === 0 ? (
        <div className="ymil4__empty">
          <i className="fa fa-image" aria-hidden="true"></i>
          <p>No images found</p>
        </div>
      ) : showDetails ? (
        <div className="ymil4__list">
          {rowsFinalReadyArr.map(item => (
            <ImageRow
              key={item.id}
              item={item}
              item_key={item.id}
              src={item.img_url_full}
              itemPack={itemPack}
              tags_arr_all={tags_arr_all}
            />
          ))}
        </div>
      ) : (
        <div className="ymil4__grid">
          {rowsFinalReadyArr.map(item => (
            <ImageCard
              key={item.id}
              item={item}
              item_key={item.id}
              src={item.img_url_full}
              itemPack={itemPack}
              tags_arr_all={tags_arr_all}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!fetching && totalCount > 0 && (
        <div className="ymil4__pagination">
          <div className="ymil4__pagination__info">
            {totalCount === 0 ? 'No images' : `Showing ${fromItem}â€“${toItem} of ${totalCount} images`}
          </div>
          <div className="ymil4__pagination__spacer" />
          <div className="ymil4__pagination__per-page">
            <span>Per page:</span>
            <select
              value={perPage}
              onChange={e => _handleChangePagination_rowsPerPage && _handleChangePagination_rowsPerPage(e.target.value)}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={''}>All</option>
            </select>
          </div>
          <div className="ymil4__pagination__controls">
            <div
              className={`ymil4__pagination__btn${!(pagination_prev_page_no > 0) ? ' ymil4__pagination__btn--disabled' : ''}`}
              onClick={() => { if (pagination_prev_page_no > 0) _handleClickPaginationPagePrev(); }}
              title="Previous page"
            >
              <i className="fa fa-chevron-left" aria-hidden="true"></i>
            </div>
            <div className="ymil4__pagination__page-info">
              {pageNo} / {pagination_pages_count || 1}
            </div>
            <div
              className={`ymil4__pagination__btn${!(pagination_next_page_no > 0) ? ' ymil4__pagination__btn--disabled' : ''}`}
              onClick={() => { if (pagination_next_page_no > 0) _handleClickPaginationPageNext(); }}
              title="Next page"
            >
              <i className="fa fa-chevron-right" aria-hidden="true"></i>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---- Upload / Add Form ----
//
// BACKEND COMPATIBILITY NOTE:
// The existing backend endpoint (image_library_upload_v3) accepts exactly one file per
// request via the `uploaded_image` field. True multi-file upload in a single request is
// NOT supported by the current backend. To support selecting/dragging multiple files at
// once, this component implements a client-side upload queue: files are uploaded one by
// one using the same single-file API call that was used before. The API contract is
// completely unchanged — each individual axios.post call is identical to the original.


// ---- Helper: load a file as a data URL for preview ----
const _read_file_as_data_url = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

// ---- Helper: validate a single file before queueing ----
const _validate_upload_file = (file) => {
  const ALLOWED_MIME = ['image/jpeg', 'image/png'];
  if (!ALLOWED_MIME.includes(file.type)) {
    return 'Only JPG and PNG images are accepted.';
  }
  return null; // valid
};

// ---- Upload Queue Item Row ----
const UploadQueueItem = ({ item, onRemove, isUploading }) => {
  const statusNode = {
    waiting: <span className="ymil4__queue-item__badge ymil4__queue-item__badge--waiting"><i className="fa fa-clock-o" aria-hidden="true"></i> Waiting</span>,
    uploading: <span className="ymil4__queue-item__badge ymil4__queue-item__badge--uploading"><UniSpinnerClassic /> Uploading&hellip;</span>,
    success: <span className="ymil4__queue-item__badge ymil4__queue-item__badge--success"><i className="fa fa-check-circle" aria-hidden="true"></i> Done</span>,
    error: <span className="ymil4__queue-item__badge ymil4__queue-item__badge--error"><i className="fa fa-times-circle" aria-hidden="true"></i> Failed</span>,
  };

  return (
    <div className={`ymil4__queue-item ymil4__queue-item--${item.status}`}>
      {/* Thumbnail */}
      <div className="ymil4__queue-item__thumb">
        {item.previewSrc
          ? <img src={item.previewSrc} alt={item.file.name} />
          : <i className="fa fa-image" aria-hidden="true"></i>
        }
      </div>

      {/* File info */}
      <div className="ymil4__queue-item__info">
        <div className="ymil4__queue-item__name" title={item.file.name}>{item.file.name}</div>
        <div className="ymil4__queue-item__size">{(item.file.size / 1024).toFixed(0)} KB</div>
        {item.errorMsg && item.status === 'error' && (
          <div className="ymil4__queue-item__errormsg">{item.errorMsg}</div>
        )}
      </div>

      {/* Status badge */}
      <div className="ymil4__queue-item__status">
        {statusNode[item.status]}
      </div>

      {/* Remove button — hidden while an upload is running or the item is actively uploading */}
      {!isUploading && item.status !== 'uploading' && (
        <button
          className="ymil4__queue-item__remove"
          title="Remove from queue"
          onClick={() => onRemove(item.id)}
        >
          <i className="fa fa-times" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
};


// ---- Upload / Add Form (multi-file, sequential backend calls) ----

const FormAddToImageLibrary = (props) => {
  const { modeAdd, setModeAdd, uploadFetching, setUploadFetching, refresh, tags_arr_all = [] } = props;
  const inputFileRef = useRef(null);

  // Each queue item: { id, file, previewSrc, status, errorMsg }
  const [queue, setQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Shared meta applied to every file in the current queue
  const [sharedMeta, setSharedMeta] = useState({ ..._zzyourmap_preset_image_library_meta });

  // Monotonically-increasing id generator for queue items
  const nextIdRef = useRef(0);
  const _next_id = () => { nextIdRef.current += 1; return nextIdRef.current; };

  // ---- Add validated files to the queue ----
  const _add_files_to_queue = async (files) => {
    const fileArr = Array.from(files);
    const newItems = await Promise.all(fileArr.map(async (file) => {
      const error = _validate_upload_file(file);
      const previewSrc = error ? null : await _read_file_as_data_url(file);
      return {
        id: _next_id(),
        file,
        previewSrc,
        status: error ? 'error' : 'waiting',
        errorMsg: error,
      };
    }));
    setQueue(q => [...q, ...newItems]);
  };

  const handleInputFileChange = async (e) => {
    const files = e.target?.files;
    if (files && files.length > 0) {
      await _add_files_to_queue(files);
    }
    // Reset so the same files can be re-selected if needed
    if (inputFileRef.current) { inputFileRef.current.value = ''; }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await _add_files_to_queue(files);
    }
  };

  const handleRemoveFromQueue = (id) => setQueue(q => q.filter(item => item.id !== id));
  const handleClearQueue = () => setQueue([]);

  // ---- Shared meta handlers ----
  const handleChangeSharedMeta = (e) => {
    const { name, value } = e.target;
    setSharedMeta(s => ({ ...s, [name]: value }));
  };

  const handleTagsAdd = (new_tag) => {
    setSharedMeta(state => {
      if (js_utils.is_string_not_empty(new_tag) && !new_tag.includes(',') && !new_tag.includes(';') && !new_tag.includes(' ')) {
        const _new_tag = new_tag.trim().toLowerCase();
        const tags_arr = js_utils.string_split_to_arr_but_exclude_empty(state.tags_csv, ',');
        if (!tags_arr.includes(_new_tag) && js_utils.is_string_not_empty(_new_tag)) {
          tags_arr.push(_new_tag);
          tags_arr.sort();
          return { ...state, tags_csv: tags_arr.join(',') };
        }
      }
      return state;
    });
  };

  const handleTagsDelete = (target_tag) => {
    setSharedMeta(state => {
      const tags_arr = js_utils.string_split_to_arr_but_exclude_empty(state.tags_csv, ',');
      if (tags_arr.includes(target_tag)) {
        return { ...state, tags_csv: tags_arr.filter(t => t !== target_tag).join(',') };
      }
      return state;
    });
  };

  // ---- Single-file upload using the EXISTING backend API ----
  // The backend accepts one file per request. We call this once per queued file.
  // FormData structure is identical to the original single-upload implementation.
  const _upload_one_file = (file, meta) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('img_alt', meta.img_alt);
      formData.append('img_title', meta.img_title);
      formData.append('note', meta.note);
      formData.append('tags_csv', meta.tags_csv);
      formData.append('size', '30');
      // `uploaded_image` is the field name the backend expects -- unchanged from original
      formData.append('uploaded_image', file, file.name);

      const upload_url = _zzyourmap_urlLib.image_library_upload_v3();
      axios.post(upload_url, formData)
        .then(response => {
          if (frfr_res_utils.is_srf(response && response.data)) {
            resolve();
          } else {
            reject(new Error('Unexpected server response.'));
          }
        })
        .catch(err => reject(err));
    });

  // ---- Process queue: upload waiting files one by one ----
  const handleUploadAll = async () => {
    const waitingItems = queue.filter(item => item.status === 'waiting');
    if (waitingItems.length === 0) return;

    setIsUploading(true);
    setUploadFetching(true);

    let anySuccess = false;

    for (const item of waitingItems) {
      setQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'uploading', errorMsg: null } : i));

      try {
        await _upload_one_file(item.file, sharedMeta);
        setQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'success' } : i));
        anySuccess = true;
      } catch (err) {
        console.error('ERROR uploading file:', item.file.name, err);
        setQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'error', errorMsg: 'Upload failed. Try again.' } : i));
      }
    }

    if (anySuccess) { refresh(); }

    setIsUploading(false);
    setUploadFetching(false);
  };

  const handleClose = () => {
    setModeAdd(false);
    setQueue([]);
    setSharedMeta({ ..._zzyourmap_preset_image_library_meta });
  };

  if (!modeAdd) return null;

  const waitingCount = queue.filter(i => i.status === 'waiting').length;
  const successCount = queue.filter(i => i.status === 'success').length;
  const errorCount = queue.filter(i => i.status === 'error').length;
  const hasQueue = queue.length > 0;
  const allDone = hasQueue && waitingCount === 0 && !isUploading;

  return (
    <div className="ymil4__upload-section">

      {/* Header */}
      <div className="ymil4__upload-header">
        <h3 className="ymil4__upload-header__title">
          <i className="fa fa-upload" aria-hidden="true"></i> Upload images
        </h3>
        {hasQueue && !isUploading && (
          <div className="ymil4__upload-header__summary">
            {waitingCount > 0 && <span className="ymil4__upload-summary-chip ymil4__upload-summary-chip--waiting">{waitingCount} waiting</span>}
            {successCount > 0 && <span className="ymil4__upload-summary-chip ymil4__upload-summary-chip--success">{successCount} done</span>}
            {errorCount > 0 && <span className="ymil4__upload-summary-chip ymil4__upload-summary-chip--error">{errorCount} failed</span>}
          </div>
        )}
        <button className="ymil4__btn-secondary ymil4__upload-header__close" onClick={handleClose}>
          <i className="fa fa-times"></i> Close
        </button>
      </div>

      {/* Drop zone */}
      <div
        className={`ymil4__dropzone${isDragging ? ' ymil4__dropzone--dragover' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputFileRef.current && inputFileRef.current.click()}
      >
        <i className="fa fa-cloud-upload ymil4__dropzone__icon" aria-hidden="true"></i>
        <p className="ymil4__dropzone__title">Drag &amp; drop images here</p>
        <p className="ymil4__dropzone__subtitle">or click to select files</p>
        <p className="ymil4__dropzone__hint">JPG &amp; PNG &mdash; multiple files supported</p>
        <input
          name="modalfileinput"
          type="file"
          accept={ACCEPT_FILE_TYPES_IMG_JPG_PNG}
          multiple={true}
          ref={inputFileRef}
          onChange={handleInputFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Upload queue */}
      {hasQueue && (
        <div className="ymil4__queue">
          <div className="ymil4__queue__header">
            <span className="ymil4__queue__title">
              <i className="fa fa-list" aria-hidden="true"></i> Queue ({queue.length} {queue.length === 1 ? 'file' : 'files'})
            </span>
            {!isUploading && (
              <button className="ymil4__btn-secondary ymil4__queue__clear-btn" onClick={handleClearQueue}>
                <i className="fa fa-trash"></i> Clear all
              </button>
            )}
          </div>
          <div className="ymil4__queue__list">
            {queue.map(item => (
              <UploadQueueItem
                key={item.id}
                item={item}
                onRemove={handleRemoveFromQueue}
                isUploading={isUploading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Shared meta */}
      {waitingCount > 0 && (
        <div className="ymil4__upload-body">
          <div className="ymil4__upload-fields">
            <div className="ymil4__upload-meta-note">
              <i className="fa fa-info-circle" aria-hidden="true"></i>
              &nbsp;These metadata fields will be applied to <strong>all</strong> {waitingCount} waiting {waitingCount === 1 ? 'file' : 'files'}.
            </div>
            <FormGenerator
              formDefinition={_zzyourmap_formDefinition_image_library_meta}
              formState={sharedMeta}
              formErrors={{}}
              handleChange={handleChangeSharedMeta}
              tagsSuggestionsByFieldName={{ tags_csv: tags_arr_all }}
            />
            <TagsEditor
              tagsValue={sharedMeta.tags_csv}
              onAdd={handleTagsAdd}
              onDelete={handleTagsDelete}
              tags_arr_all={tags_arr_all}
            />
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="ymil4__upload-actions">
        {waitingCount > 0 && (
          <button
            className="ymil4__btn-primary"
            disabled={isUploading}
            onClick={handleUploadAll}
          >
            <i className="fa fa-upload"></i>
            &nbsp;{isUploading ? 'Uploading...' : `Upload ${waitingCount} ${waitingCount === 1 ? 'file' : 'files'}`}
          </button>
        )}
        {allDone && errorCount === 0 && (
          <button className="ymil4__btn-primary" onClick={handleClose}>
            <i className="fa fa-check"></i> Done
          </button>
        )}
        {errorCount > 0 && !isUploading && (
          <button
            className="ymil4__btn-secondary"
            onClick={() => setQueue(q => q.map(i => i.status === 'error' ? { ...i, status: 'waiting', errorMsg: null } : i))}
          >
            <i className="fa fa-refresh"></i> Retry failed
          </button>
        )}
        <button className="ymil4__btn-secondary" onClick={handleClose}>
          <i className="fa fa-times"></i> Cancel
        </button>
      </div>

    </div>
  );
};


// ---- DEFAULT EXPORT COMPONENT ----

const YourmapImageLibraryV4 = (props) => {
  const {
    route_freshness,
    setUniModalPortableContent_3,
    setUniModalPortableClose_3,
  } = props.appPack;
  const { enableSelectSingular, enableSelectMultiple, enableAdminFunc } = props;
  const cbDialogCancel = props.cbDialogCancel;

  // State
  const [modeAdd, setModeAdd] = useState(false);
  const [isRecycleBin, setIsRecycleBin] = useState(false);
  const [uploadFetching, setUploadFetching] = useState(false);
  const [tagsFilterObj, setTagsFilterObj] = useState({});
  const [tagSearch, setTagSearch] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [dataFreshness, setDataFreshness] = useState(0);
  const dataFreshnessRefresh = () => setDataFreshness(n => n + 1);

  const [imageLibEntitiesDmd, setImageLibEntitiesDmd] = useState(dmd_utils.create_type_arr());
  const [imageLibEntitiesDeletedDmd, setImageLibEntitiesDeletedDmd] = useState(dmd_utils.create_type_arr());
  const [imageLibTagsArrAllDmd, setImageLibTagsArrAllDmd] = useState(dmd_utils.create_type_arr(..._zzyourmap_lib_image_library_tags_default)); // TODO
  const [imageLibTagsArrUsedDmd, setImageLibTagsArrUsedDmd] = useState(dmd_utils.create_type_arr(..._zzyourmap_lib_image_library_tags_default)); // TODO
  const fetching = dmd_utils.is_fetching(imageLibEntitiesDmd);
  const tags_arr_all = dmd_utils.dmd_to_always_arr(imageLibTagsArrAllDmd);
  const tags_arr_used = dmd_utils.dmd_to_always_arr(imageLibTagsArrUsedDmd);
  //
  const [selectedLibItems, setSelectedLibItems] = useState([]);
  const [selectedLibItemSingular, setSelectedLibItemSingular] = useState(null);

  const cbOnSelectionChange = (keys) => setSelectedLibItems(keys);
  const cbOnSelectionChangeSingular = (key) => setSelectedLibItemSingular(key);

  const canPick =
    (enableSelectSingular === true && selectedLibItemSingular !== null) ||
    (enableSelectMultiple === true && selectedLibItems.length > 0);

  // Pick handlers
  const cbPickSingleImage = () => {
    if (enableSelectSingular === true && typeof props.cbPickSingleImage === 'function') {
      if (fetching !== true && selectedLibItemSingular !== null) {
        const arr_for_picking = dmd_utils.dmd_to_always_arr(imageLibEntitiesDmd);
        let bingo_item = null;
        arr_for_picking.forEach(item => {
          if (!bingo_item && item.id === selectedLibItemSingular) { bingo_item = item; }
        });
        if (bingo_item) {
          props.cbPickSingleImage(bingo_item, 'second arg');
        }
        cbDialogCancel?.();
      }
    }
  };

  const cbPickMultipleImages = () => {
    if (enableSelectMultiple === true && typeof props.cbPickMultipleImages === 'function') {
      if (fetching !== true && selectedLibItems) {
        const arr_for_picking = dmd_utils.dmd_to_always_arr(imageLibEntitiesDmd);
        const bingo_items_arr = arr_for_picking.filter(item => selectedLibItems.includes(item.id));
        props.cbPickMultipleImages(bingo_items_arr, 'second arg');
        cbDialogCancel?.();
      }
    }
  };

  const handlePick = () => {
    if (enableSelectSingular) cbPickSingleImage();
    if (enableSelectMultiple) cbPickMultipleImages();
  };

  const handleCloseModal = () => {
    if (typeof cbDialogCancel === 'function') {
      cbDialogCancel();
      return;
    }
    if (typeof setUniModalPortableClose_3 === 'function') {
      setUniModalPortableClose_3();
    }
  };

  // Data fetching
  const refresh = () => {
    setImageLibEntitiesDmd(state => dmd_utils.update_fetching(state, true));
    setImageLibEntitiesDeletedDmd(state => dmd_utils.update_fetching(state, true));
    dataFreshnessRefresh();
    _zzyourmap_ajax.image_lib_get_all()
      .then(response => {
        if (frfr_res_utils.is_srf(response?.data)) {
          const res_payload = frfr_res_utils.srf_extract_payload(response.data);
          const rows = res_payload?.rows;
          if (Array.isArray(rows)) {
            const [entities, entities_deleted, tags_arr_all, tags_arr_used] = _zzyourmap_prepare_image_library_entities(rows);
            setImageLibEntitiesDmd(state => dmd_utils.update_arr(state, entities, false));
            setImageLibEntitiesDeletedDmd(state => dmd_utils.update_arr(state, entities_deleted, false));
            setImageLibTagsArrAllDmd(state => dmd_utils.update_arr(state, tags_arr_all, false));
            setImageLibTagsArrUsedDmd(state => dmd_utils.update_arr(state, tags_arr_used, false));
            dataFreshnessRefresh();
          }
        }
      })
      .catch(err => {
        console.error('ERROR image_lib_get_all', err);
        dataFreshnessRefresh();
      });
  };

  useEffect(() => { refresh(); }, [route_freshness]);

  //
  useEffect(() => { // reset tags filters on tag list change
    setTagsFilterObj({});
  }, [tags_arr_used]);
  //
  // Tag filter
  const _handleChangeTagsFilter = (tag) => {
    setTagsFilterObj(current => {
      const updated = { ...current };
      if (updated[tag] === true) { delete updated[tag]; } else { updated[tag] = true; }
      return updated;
    });
  };

  useEffect(() => { dataFreshnessRefresh(); }, [tagsFilterObj]);

  // Reset to page 1 whenever sort order changes
  useEffect(() => { dataFreshnessRefresh(); }, [sortBy]);

  // Delete / Undelete
  const _handleClickDeleteImage = (item_key) => {
    if (item_key > 0) {
      _zzyourmap_ajax.image_lib_update_flagdeleted({ flag_deleted: true }, item_key)
        .then(response => {
          if (frfr_res_utils.is_srf(response?.data)) { refresh(); }
        })
        .catch(err => console.error('ERROR image_lib_update_flagdeleted:', err));
    }
  };

  const _handleClickUndeleteImage = (item_key) => {
    if (item_key > 0) {
      _zzyourmap_ajax.image_lib_update_undelete({ flag_deleted: false }, item_key)
        .then(response => {
          if (frfr_res_utils.is_srf(response?.data)) { refresh(); }
        })
        .catch(err => console.error('ERROR image_lib_update_undelete:', err));
    }
  };

  // Update meta
  const cbSubmitUpdateImageMeta = (entbox, payload) => {
    const id = payload.id;
    if (id > 0) {
      _zzyourmap_ajax.image_lib_update_meta(entbox, id)
        .then(response => {
          if (frfr_res_utils.is_srf(response?.data)) {
            refresh();
            setUniModalPortableClose_3();
          }
        })
        .catch(err => console.error('ERROR image_lib_update_meta:', err));
    }
  };

  // Build display array
  let arr = dmd_utils.dmd_to_always_arr(isRecycleBin ? imageLibEntitiesDeletedDmd : imageLibEntitiesDmd);

  const filtered_arr = arr.filter((item) => {
    if (Object.keys(tagsFilterObj).length === 0) return true;
    if (js_utils.is_string_not_empty(item.tags_csv)) {
      let _in = true;
      Object.keys(tagsFilterObj).forEach(tag => {
        if (tag === 'UNTAGGED') { _in = false; }
        else if (!item.tags_csv.includes(tag)) { _in = false; }
        if (tagsFilterObj['UNTAGGED'] === true) { _in = false; }
      });
      return _in;
    } else {
      if (tagsFilterObj['UNTAGGED'] === true) {
        return Object.keys(tagsFilterObj).length <= 1;
      }
      return false;
    }
  });

  // Apply sort after filtering and before pagination (pagination is handled inside useFrfrViewMultimode)
  const sorted_arr = _sort_arr(filtered_arr, sortBy);

  const viewArgs = {
    _fetching: fetching,
    rowsData: sorted_arr,
    dataFreshness,
    cbOnSelectionChange,
    cbOnSelectionChangeSingular,
  };

  // Sidebar: filtered tag list
  const tagSearchLower = tagSearch.toLowerCase();
  const visibleTags = tagSearchLower
    ? tags_arr_all.filter(t => t.toLowerCase().includes(tagSearchLower))
    : tags_arr_all;

  const activeTagsCount = Object.keys(tagsFilterObj).filter(t => t !== 'UNTAGGED').length;

  return (
    <div className="ymil4">

      {/* ---- TOOLBAR ---- */}
      <div className="ymil4__toolbar">
        <h2 className="ymil4__title">Image Library</h2>

        {/* Tabs: Active / Trash */}
        <div className="ymil4__tabs">
          <button
            className={`ymil4__tab${!isRecycleBin ? ' ymil4__tab--active' : ''}`}
            onClick={() => setIsRecycleBin(false)}
          >
            <i className="fa fa-images" aria-hidden="true"></i>
            Active
          </button>
          {enableAdminFunc && (
            <button
              className={`ymil4__tab ymil4__tab--trash${isRecycleBin ? ' ymil4__tab--active' : ''}`}
              onClick={() => setIsRecycleBin(true)}
            >
              <i className="fa fa-trash" aria-hidden="true"></i>
              Trash
            </button>
          )}
        </div>

        {/* Sort control — aligned right, in the same toolbar row as the tabs */}
        <div className="ymil4__sort-control">
          <label className="ymil4__sort-control__label" htmlFor="ymil4-sort-select">
            <i className="fa fa-sort" aria-hidden="true"></i>
            Sort by:
          </label>
          <select
            id="ymil4-sort-select"
            className="ymil4__sort-control__select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="ymil4__toolbar-spacer" />

        <div className="ymil4__toolbar-right">
          {/* Show details toggle */}
          <label className="ymil4__toggle">
            <input
              type="checkbox"
              checked={showDetails}
              onChange={() => setShowDetails(v => !v)}
            />
            <span className="ymil4__toggle__track"></span>
            <span className="ymil4__toggle__label">Details</span>
          </label>

          {/* Refresh */}
          <button className="ymil4__icon-btn" onClick={refresh} title="Refresh">
            <i className="fa fa-refresh" aria-hidden="true"></i>
          </button>

          {/* Upload button */}
          <button
            className="ymil4__upload-btn"
            onClick={() => setModeAdd(true)}
            disabled={modeAdd}
            title="Upload new image"
          >
            <i className="fa fa-upload" aria-hidden="true"></i>
            <span>Upload</span>
          </button>

          {/* Pick CTA */}
          {(enableSelectSingular || enableSelectMultiple) && (
            <button
              className="ymil4__pick-btn"
              disabled={!canPick}
              onClick={handlePick}
              title="Pick selected image"
            >
              <i className="fa fa-check" aria-hidden="true"></i>
              <span>Pick selected</span>
            </button>
          )}

          <button
            className="ymil4__icon-btn ymil4__close-btn"
            onClick={handleCloseModal}
            title="Close"
            aria-label="Close image library"
          >
            <i className="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* ---- BODY: sidebar + main content ---- */}
      <div className="ymil4__layout">

        {/* Sidebar */}
        <aside className="ymil4__sidebar">
          <div>
            <div className="ymil4__sidebar-heading">
              Filter by tags
              {activeTagsCount > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontWeight: 700,
                  background: '#2563eb', color: 'white',
                  borderRadius: '50%', padding: '1px 5px',
                }}>
                  {activeTagsCount}
                </span>
              )}
            </div>
            <input
              className="ymil4__tag-search"
              type="text"
              placeholder="Search tags..."
              value={tagSearch}
              onChange={e => setTagSearch(e.target.value)}
            />
            <div className="ymil4__tag-list">
              {visibleTags.map(tag => (
                <label
                  key={tag}
                  className={`ymil4__tag-item${tagsFilterObj[tag] === true ? ' ymil4__tag-item--active' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={tagsFilterObj[tag] === true}
                    onChange={() => _handleChangeTagsFilter(tag)}
                  />
                  <span className="ymil4__tag-item__name">{tag}</span>
                </label>
              ))}
              <label
                className={`ymil4__tag-item ymil4__tag-item--special${tagsFilterObj['UNTAGGED'] === true ? ' ymil4__tag-item--active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={tagsFilterObj['UNTAGGED'] === true}
                  onChange={() => _handleChangeTagsFilter('UNTAGGED')}
                />
                <span className="ymil4__tag-item__name">Untagged</span>
              </label>
              {activeTagsCount > 0 && (
                <button
                  className="ymil4__btn-secondary"
                  style={{ marginTop: 8, padding: '4px 10px', fontSize: 12 }}
                  onClick={() => setTagsFilterObj({})}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="ymil4__main">
          <ImageLibraryView
            viewArgs={viewArgs}
            tags_arr_all={tags_arr_all}
            tags_arr_used={tags_arr_used}
            enableSelectSingular={enableSelectSingular}
            enableSelectMultiple={enableSelectMultiple}
            enableAdminFunc={enableAdminFunc}
            isRecycleBin={isRecycleBin}
            showDetails={showDetails}
            _handleClickDeleteImage={_handleClickDeleteImage}
            _handleClickUndeleteImage={_handleClickUndeleteImage}
            cbSubmitUpdateImageMeta={cbSubmitUpdateImageMeta}
            setUniModalPortableContent_3={setUniModalPortableContent_3}
            setUniModalPortableClose_3={setUniModalPortableClose_3}
          />
        </div>
      </div>

      {/* ---- UPLOAD ZONE ---- */}
      <FormAddToImageLibrary
        appPack={props.appPack}
        modeAdd={modeAdd}
        setModeAdd={setModeAdd}
        uploadFetching={uploadFetching}
        setUploadFetching={setUploadFetching}
        refresh={refresh}
        tags_arr_all={tags_arr_all}
      />
    </div>
  );
};

export default YourmapImageLibraryV4;
